import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandingApi } from '@/api/supports';
import { TenantBranding } from '@/types';
import { useTenantContext } from './useTenantContext';
import { hexToHslParts, readableForeground } from '@/lib/color';

// Theme vars we override from tenant branding. Listed so we can reset them all
// (e.g. when a SuperAdmin exits impersonation) and fall back to the class theme.
const BRAND_OVERRIDES = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-ring',
  '--brand-primary',
  '--brand-secondary',
  '--brand-accent',
];

export function useBrandingTheme() {
  const { isSuperAdmin, impersonatedTenantId } = useTenantContext();
  const isImpersonating = isSuperAdmin && impersonatedTenantId != null;
  // Fetch branding for a real tenant admin, OR a SuperAdmin impersonating a
  // tenant. A SuperAdmin on the platform menu uses the Maksab brand (no fetch).
  const shouldApply = !isSuperAdmin || isImpersonating;

  const { data: branding } = useQuery<TenantBranding>({
    // Key by impersonated tenant so switching tenants refetches its branding.
    queryKey: ['branding', impersonatedTenantId ?? null],
    queryFn: brandingApi.get,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    enabled: shouldApply,
  });

  const websiteBranding = shouldApply ? branding?.website : undefined;

  useEffect(() => {
    const root = document.documentElement;

    // No tenant branding in effect (platform SuperAdmin, or exited
    // impersonation): strip any overrides so the class theme takes over.
    if (!websiteBranding) {
      BRAND_OVERRIDES.forEach((p) => root.style.removeProperty(p));
      return;
    }

    const primaryHsl = hexToHslParts(websiteBranding.primaryColor);
    if (primaryHsl) {
      // Buttons / focus rings / links and the active sidebar item adopt the
      // tenant's primary; foreground flips to stay legible on that color.
      const fg = readableForeground(websiteBranding.primaryColor);
      root.style.setProperty('--primary', primaryHsl);
      root.style.setProperty('--primary-foreground', fg);
      root.style.setProperty('--ring', primaryHsl);
      root.style.setProperty('--sidebar-primary', primaryHsl);
      root.style.setProperty('--sidebar-primary-foreground', fg);
      root.style.setProperty('--sidebar-ring', primaryHsl);
    }

    // Keep the raw brand vars too (consumed by any brand-specific styling).
    if (websiteBranding.primaryColor) root.style.setProperty('--brand-primary', websiteBranding.primaryColor);
    if (websiteBranding.secondaryColor) root.style.setProperty('--brand-secondary', websiteBranding.secondaryColor);
    if (websiteBranding.accentColor) root.style.setProperty('--brand-accent', websiteBranding.accentColor);

    // Reset on unmount / before re-applying for a different tenant.
    return () => {
      BRAND_OVERRIDES.forEach((p) => root.style.removeProperty(p));
    };
  }, [websiteBranding]);

  return {
    branding: websiteBranding,
    logoUrl: websiteBranding?.logoUrl,
    appName: websiteBranding?.appName,
    appNameArabic: websiteBranding?.appNameArabic,
  };
}
